import React, { useState } from 'react'
import { Icon, Tooltip, Button } from 'antd'
import { ImagePreview } from '../../../../components'
import LocationSelectionModal from './components/LocationSelectionModal'
import CharacterSelectionModal from './components/CharacterSelectionModal'
import './EntitySelectorList.scss'

const EntitySelectorList = ({
  activePrompt,
  onUpdatePrompt, // function(newPrompt)
  formData,
  disabled = false
}) => {
  const [locationModalVisible, setLocationModalVisible] = useState(false)
  const [tagToReplace, setTagToReplace] = useState(null)
  const [charModalVisible, setCharModalVisible] = useState(false)
  const [charTagToReplace, setCharTagToReplace] = useState(null)

  // 1. Location Parsing
  const getAllLocationsFromPrompt = text => {
    if (!text) return []
    const matches = text.matchAll(/__(location[\d\.]+)__/g)
    return Array.from(matches, m => m[0])
  }

  // 2. Character Parsing
  const getAllCharactersFromPrompt = text => {
    if (!text) return []
    const allTags = Array.from(text.matchAll(/__([\s\S]+?)__/g), m => m[0])
    return allTags.filter(tag => !tag.match(/^__location[\d\.]+__$/))
  }

  const detectedLocations = getAllLocationsFromPrompt(activePrompt)
  const detectedCharacters = getAllCharactersFromPrompt(activePrompt)

  const handleLocationSelect = key => {
    const rawKey = key.replace(/^__/, '').replace(/__$/, '')
    const newTag = `__${rawKey}__`
    let newText = activePrompt || ''

    if (tagToReplace) {
      newText = newText.replace(tagToReplace, newTag)
    } else {
      newText = `${newTag}, ${newText}`
    }

    onUpdatePrompt && onUpdatePrompt(newText)
    setLocationModalVisible(false)
    setTagToReplace(null)
  }

  const openLocationModal = fullTag => {
    setTagToReplace(fullTag)
    setLocationModalVisible(true)
  }

  const handleCharacterSelect = name => {
    const newTag = `__${name}__`
    let newText = activePrompt || ''

    if (charTagToReplace) {
      newText = newText.replace(charTagToReplace, newTag)
    } else {
      newText = `${newTag}, ${newText}`
    }

    onUpdatePrompt && onUpdatePrompt(newText)
    setCharModalVisible(false)
    setCharTagToReplace(null)
  }

  const openCharModal = fullTag => {
    setCharTagToReplace(fullTag)
    setCharModalVisible(true)
  }

  // Mapping to Entities
  const locationEntities = detectedLocations.map(fullTag => {
    const rawKey = fullTag.replace(/^__/, '').replace(/__$/, '')
    const refs = formData.locationReference || {}
    const refKey = refs[fullTag] ? fullTag : rawKey
    const reference = refs[refKey]
    const locationImage =
      reference &&
      (reference.uri || reference.url || reference.image || reference.src)
    const locationDesc = formData.locationMap && formData.locationMap[fullTag]
    const isValid = !!locationDesc

    return {
      key: `loc-${fullTag}`,
      tag: fullTag,
      image: locationImage,
      title: rawKey,
      subtitle: locationDesc,
      isValid,
      icon: 'environment',
      onAction: openLocationModal
    }
  })

  const characterEntities = detectedCharacters.map(fullTag => {
    const rawName = fullTag.replace(/^__/, '').replace(/__$/, '')
    const characterList = formData.characterList || []
    const selectedCharacters = formData.selectedCharacter || []

    const mapping = selectedCharacters.find(
      m => m.original === fullTag || m.current === fullTag
    )
    const resolvedName = mapping
      ? mapping.current.replace(/^__|__$/g, '')
      : rawName

    const character = characterList.find(c => c.name === resolvedName)
    const charImage = character?.attributes?.profile_pic
    const isValid = !!character

    return {
      key: `char-${fullTag}`,
      tag: fullTag,
      image: charImage,
      title: rawName,
      subtitle: character ? character.name : null,
      isValid,
      icon: 'user',
      onAction: openCharModal
    }
  })

  const entities = [...locationEntities, ...characterEntities]
  const hasEntities = entities && entities.length > 0

  return (
    <>
      <div className="entity-selectors-container">
        <div className={`selectors-tray ${!hasEntities ? 'empty' : ''}`}>
          <div className="tray-header">
            <span>
              Context Entities {hasEntities && `(${entities.length})`}
            </span>
          </div>
          <div className="tray-content">
            {hasEntities ? (
              entities.map((entity, index) => (
                <div key={`${entity.key}-${index}`} className="selector-item">
                  <div className="selector-content-wrapper">
                    {entity.isValid ? (
                      <>
                        {entity.image ? (
                          <div className="entity-avatar">
                            <ImagePreview
                              src={entity.image}
                              alt={entity.title}
                            />
                          </div>
                        ) : (
                          <div
                            className={`entity-avatar placeholder ${
                              entity.icon === 'environment'
                                ? 'environment'
                                : 'user'
                            }`}
                          >
                            <Icon type={entity.icon || 'question'} />
                          </div>
                        )}
                        <div className="entity-info">
                          <span className="entity-tag">{entity.tag}</span>
                          {entity.subtitle && (
                            <Tooltip title={entity.subtitle}>
                              <span className="entity-desc">
                                {entity.subtitle}
                              </span>
                            </Tooltip>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="entity-info error">
                        <span className="error-title">
                          <Icon type="warning" />
                          Unknown: {entity.tag}
                        </span>
                      </div>
                    )}
                  </div>

                  <Button
                    size="small"
                    type="link"
                    danger={!entity.isValid}
                    onClick={() => entity.onAction?.(entity.tag)}
                    disabled={disabled}
                    className="selector-action-btn"
                  >
                    {entity.isValid ? 'Change' : 'Fix'}
                  </Button>
                </div>
              ))
            ) : (
              <div className="tray-empty-state">
                <Icon type="info-circle" />
                <span>No location or character tags detected in prompt.</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <LocationSelectionModal
        visible={locationModalVisible}
        onCancel={() => {
          setLocationModalVisible(false)
          setTagToReplace(null)
        }}
        onSelect={handleLocationSelect}
        locationMap={formData.locationMap}
        locationReference={formData.locationReference}
        currentLocationKey={tagToReplace}
      />
      <CharacterSelectionModal
        visible={charModalVisible}
        onCancel={() => {
          setCharModalVisible(false)
          setCharTagToReplace(null)
        }}
        onSelect={handleCharacterSelect}
        characterList={formData.characterList}
        selectedCharacter={formData.selectedCharacter}
        currentCharacterKey={charTagToReplace}
      />
    </>
  )
}

export default EntitySelectorList
